import { BENG_LINES, type BengLine } from "./benglas-data";

export interface BengPageContent {
  eyebrow: string;
  h1: string;
  seoTitle: string;
  seoDescription: string;
  canonicalPath: string;
  description: string;
  kenmerken: string[];
  toepassingen: string[];
  faq: { q: string; a: string }[];
  breadcrumbLabel: string;
}

/** Gedeelde basisteksten (kenmerken/toepassingen/faq) voor de gecoate lijnen. */
const BASE = {
  description:
    "BENGglas (LandVac) is het dunste en lichtste vacuümglas ter wereld. Twee glasruiten worden vacuüm-hermetisch verbonden met een microscopisch dun spacer-raster — geen gasvulling, geen verlies van isolatiewaarde in de tijd. Een U-waarde van 0,35 W/m²K is mogelijk in slechts enkele millimeters dikte. Ideaal voor renovatie van monumenten, slanke kozijnen en elk project waar triple glas te dik of te zwaar is.",
  kenmerken: [
    "Dunste vacuümglas ter wereld (6,7–16,3 mm)",
    "U-waarde 0,35–0,50 W/m²K",
    "Gehard veiligheidsglas — 20 jaar garantie",
    "Gecertificeerd: ift Rosenheim, TÜV, NEN 3569, RoHS",
    "Geen gasvulling — houdt U-waarde eeuwig vast",
    "Maximaal 2350 × 4150 mm",
  ],
  toepassingen: [
    "Renovatie & monumenten (past in bestaande kozijnen)",
    "HR-renovatieramen en dakkapellen",
    "Vloerverwarming-gecombineerde gevels (lage Ug nodig)",
    "Slanke staal- en aluminiumkozijnen",
    "Daklichten en lichtstraten",
    "Geluidsisolatie langs drukke wegen (met akoestische binnenruit)",
  ],
  faq: [
    {
      q: "Wat is BENGglas (vacuümglas)?",
      a: "BENGglas is LandVac vacuümglas: twee glasruiten die vacuüm-hermetisch verbonden zijn zonder gasvulling. Hierdoor is een U-waarde tot 0,35 W/m²K mogelijk in een dikte vanaf 6,7mm — dunner dan gewoon dubbelglas.",
    },
    {
      q: "Kan ik BENGglas gebruiken in bestaande kozijnen?",
      a: "Ja. Dankzij de geringe dikte (6,7–16,3mm) past BENGglas in veel bestaande kozijnen zonder aanpassing. Ideaal voor monumenten en renovatieprojecten.",
    },
    {
      q: "Wat is de levertijd van BENGglas?",
      a: "De indicatieve levertijd is 3–5 weken na orderbevestiging. BENGglas is maatwerk en wordt speciaal voor u geproduceerd.",
    },
    {
      q: "Wat zijn de verzendkosten voor vacuümglas?",
      a: "Vacuümglas wordt via gespecialiseerd glazentransport geleverd: €195 excl. btw binnen Nederland, €225 excl. btw naar België.",
    },
    {
      q: "Valt BENGglas onder het herroepingsrecht?",
      a: "Nee. BENGglas wordt op maat geproduceerd en valt daardoor buiten het wettelijk herroepingsrecht.",
    },
  ],
};

/** Pagina-content voor een gecoate lijn (Single/Double/Triple Coated) uit de prijslijst-specs. */
function coatedContent(line: BengLine, slug: string): BengPageContent {
  const info = BENG_LINES[line];
  const g = info.gValue != null ? `${info.gValue}`.replace(".", ",") : "—";
  return {
    eyebrow: "Vacuümglas op maat",
    h1: `BENGglas ${info.label} — vacuümglas`,
    seoTitle: `BENGglas ${info.label} vacuümglas op maat — LandVac`,
    seoDescription: `BENGglas ${info.label} (LandVac vacuümglas): U-waarde ${`${info.uValue}`.replace(".", ",")}, lichttransmissie ${info.lta}%, g-waarde ${g}. Ultradun, op maat, 20 jaar garantie.`,
    canonicalPath: `/vacuumglas/${slug}`,
    breadcrumbLabel: info.label,
    description: `BENGglas ${info.label}: ${info.description.toLowerCase().replace(/\.$/, "")} — U-waarde ≈ ${`${info.uValue}`.replace(".", ",")} W/m²K, lichttransmissie ${info.lta}%, g-waarde ${g}. ${BASE.description}`,
    kenmerken: BASE.kenmerken,
    toepassingen: BASE.toepassingen,
    faq: BASE.faq,
  };
}

export const BENG_PAGE_CONTENT: Record<BengLine, BengPageContent> = {
  "single-coated": coatedContent("single-coated", "single-coated"),
  "double-coated": coatedContent("double-coated", "double-coated"),
  "triple-coated": coatedContent("triple-coated", "triple-coated"),

  monu: {
    eyebrow: "Monumentenglas op maat",
    h1: "BENGglas Monu — vacuümglas voor monumenten",
    seoTitle: "BENGglas Monu — extra helder monumentenglas op maat",
    seoDescription: "BENGglas Monu: extra helder vacuümglas speciaal voor monumenten. 8,3–16,3mm, lichttransmissie 83%, directe prijs.",
    canonicalPath: "/vacuumglas/monu",
    breadcrumbLabel: "Monu",
    description:
      "BENGglas Monu is speciaal ontwikkeld voor monumenten en beschermde gebouwen. Het extra heldere glas geeft maximale lichtdoorval (83%) en een neutraal, authentiek aanzicht. Met een dikte vanaf 8,3mm past het glas in vrijwel elk bestaand kozijn — zonder sloopwerk of aanpassing van de dagmaat.",
    kenmerken: [
      "Extra helder — lichttransmissie 83%",
      "Beschikbaar in 8,3 / 10,3 / 12,3 / 16,3 mm",
      "U-waarde ≈ 0,50 W/m²K",
      "Gehard veiligheidsglas, 20 jaar garantie",
      "Geschikt voor smalle spouwen en monumentale kozijnen",
    ],
    toepassingen: [
      "Rijksmonumenten en beschermde stadsgezichten",
      "Kerken, musea en historische panden",
      "Kantoorpanden met monument-status",
      "Renovatie zonder aantasting van het aanzicht",
    ],
    faq: [
      {
        q: "Wat maakt Monu anders dan de gecoate BENGglas-lijnen?",
        a: "Monu is gemaakt van extra helder glas met maximale lichtdoorval (83%) en een hoge g-waarde — het glas oogt neutraal en authentiek, precies wat bij monumenten gewenst is.",
      },
      {
        q: "Is Monu geschikt voor monumenten die vergunning vereisen?",
        a: "BENGglas Monu is speciaal ontwikkeld voor gebruik in en bij monumenten. De geringe dikte zorgt dat het visueel neutraal is. Of een vergunning nodig is, hangt af van het specifieke monument.",
      },
      {
        q: "Wat zijn de levertijd en verzendkosten?",
        a: "Levertijd 3–5 weken (maatwerk). Verzending: NL €195 / BE €225 excl. btw per order.",
      },
    ],
  },

  "monu-restover": {
    eyebrow: "Monumentenglas op maat",
    h1: "Monu Restover — vacuümglas met authentiek oud aanzicht",
    seoTitle: "BENGglas Monu Restover — restauratie-vacuümglas op maat",
    seoDescription: "BENGglas Monu Restover: vacuümglas met de authentieke uitstraling van oud vensterglas. 8,3 of 10,3mm, max 1500×2500mm, directe prijs.",
    canonicalPath: "/vacuumglas/monu-restover-500",
    breadcrumbLabel: "Monu Restover",
    description:
      "Monu Restover combineert moderne vacuümisolatie met de authentieke, licht golvende uitstraling van historisch vensterglas. Daarmee blijft het karakteristieke aanzicht van een monument behouden, terwijl u isoleert op het niveau van modern HR-glas. Beschikbaar in 8,3mm en 10,3mm, tot 1500 × 2500 mm.",
    kenmerken: [
      "Authentieke uitstraling van oud vensterglas",
      "Beschikbaar in 8,3mm en 10,3mm",
      "Lichttransmissie 75% · U-waarde ≈ 0,50 W/m²K",
      "Gehard veiligheidsglas, 20 jaar garantie",
      "Maximaal 1500 × 2500 mm",
    ],
    toepassingen: [
      "Restauratie van rijksmonumenten",
      "Herstel van historische gevels met authentiek glasbeeld",
      "Kerken, musea en beschermde panden",
      "Vervanging van enkel historisch glas zonder gezichtsverlies",
    ],
    faq: [
      {
        q: "Wat is het verschil tussen Monu en Monu Restover?",
        a: "Monu is extra helder en visueel neutraal; Restover heeft bewust de licht golvende, authentieke uitstraling van oud vensterglas — voor restauraties waar het historische glasbeeld behouden moet blijven.",
      },
      {
        q: "Wat is het verschil tussen 8,3mm en 10,3mm?",
        a: "De 10,3mm-variant is geschikt voor kozijnen met een iets diepere rabat. De prijs per m² is gelijk; kies de dikte die bij uw kozijn past.",
      },
      {
        q: "Wat is de maximale afmeting?",
        a: "Monu Restover is leverbaar tot 1500 × 2500 mm (kleiner dan de overige lijnen).",
      },
    ],
  },

  "solar-control": {
    eyebrow: "Vacuümglas met zonwering",
    h1: "BENGglas Solar Control — vacuümglas met lage g-waarde",
    seoTitle: "BENGglas Solar Control — vacuümglas met zonwering op maat",
    seoDescription: "BENGglas Solar Control: vacuümglas met g-waarde 0,29 voor maximale zonwering. 10,3–16,3mm, directe prijs.",
    canonicalPath: "/vacuumglas/solar-control",
    breadcrumbLabel: "Solar Control",
    description:
      "BENGglas Solar Control combineert de dunheid en isolatiewaarde van vacuümglas met een zeer lage g-waarde (0,29). Hierdoor wordt zonnewarmte tegengehouden terwijl er voldoende daglicht toetreedt. Ideaal voor glasgevels, dakramen en oriëntaties met hoge zonbelasting. Beschikbaar in 10,3 / 12,3 / 16,3 mm.",
    kenmerken: [
      "g-waarde 0,29 — maximale zonwering",
      "Beschikbaar in 10,3 / 12,3 / 16,3 mm",
      "U-waarde ≈ 0,50 W/m²K · lichttransmissie 45%",
      "Gehard veiligheidsglas, 20 jaar garantie",
      "Directe prijs, configureerbaar op maat",
    ],
    toepassingen: [
      "Glasgevels en curtain wall",
      "Dakramen en dakkapellen op het zuiden",
      "Kantoorpanden met hoge zonbelasting",
      "Serres en wintertuinen",
    ],
    faq: [
      {
        q: "Wat is het verschil tussen Solar Control en de gecoate lijnen?",
        a: "Solar Control heeft een coating die zonnewarmte (infrarood) sterk tegenhoudt — de g-waarde is met 0,29 de laagste van het assortiment. Dat is ideaal als oververhitting een risico is.",
      },
      {
        q: "Beïnvloedt Solar Control de lichtdoorval?",
        a: "Ja, de lichttransmissie is 45% — het glas is duidelijk getint. Daar staat maximale zonwering tegenover.",
      },
      {
        q: "Wat zijn de levertijd en verzendkosten?",
        a: "Levertijd 3–5 weken (maatwerk). Verzending: NL €195 / BE €225 excl. btw per order.",
      },
    ],
  },

  sun: {
    eyebrow: "Vacuümglas op projectbasis",
    h1: "BENGglas Sun — licht & warmte op maat afgestemd",
    seoTitle: "BENGglas Sun — vacuümglas met warmtewering",
    seoDescription: "BENGglas Sun: 14mm vacuümglas waarvan lichtdoorval en g-waarde per project worden afgestemd. Prijs op aanvraag.",
    canonicalPath: "/vacuumglas/sun",
    breadcrumbLabel: "Sun",
    description:
      "BENGglas Sun is de projectvariant waarbij lichtdoorval en zontoetreding op uw situatie worden afgestemd. De opbouw (14mm) wordt per project bepaald, tot 1500 × 2500 mm. Prijs op aanvraag — vraag een offerte aan met uw maten.",
    kenmerken: [
      "Lichtdoorval en g-waarde aanpasbaar per project",
      "14mm totale dikte",
      "U-waarde ≈ 0,50 W/m²K",
      "Maximaal 1500 × 2500 mm",
      "Prijs op aanvraag (offerte binnen 1 werkdag)",
    ],
    toepassingen: [
      "Lichtstraten en daklichten",
      "Serres en glazen gevels",
      "Dakkapellen met daglicht-prioriteit",
      "Winkelruimtes en tentoonstellingsgebouwen",
    ],
    faq: [
      {
        q: "Waarom is BENGglas Sun op aanvraag?",
        a: "Bij Sun worden de lichtdoorval en g-waarde per project afgestemd — dat vereist maatwerk-advies. Na uw aanvraag ontvangt u binnen één werkdag een offerte.",
      },
      {
        q: "Wat is de maximale afmeting voor Sun?",
        a: "Sun is leverbaar tot 1500 × 2500 mm.",
      },
      {
        q: "Wat zijn de verzendkosten?",
        a: "Verzending via gespecialiseerd glazentransport: NL €195 / BE €225 excl. btw per order.",
      },
    ],
  },
};

/** Een vacuümglas-variant zoals die los op de nav + landing staat — één productlijn per pagina. */
export interface BengVariant {
  slug: string;
  path: string;
  /** Korte naam (nav/landing/kaart). */
  label: string;
  line: BengLine;
  content: BengPageContent;
  /** Productfoto (nav/landing-kaart + configurator-galerij). */
  image: string;
}

/** De 7 vacuümglas-varianten (exact de prijslijst), in nav/landing-volgorde. */
export const BENG_VARIANTS: BengVariant[] = [
  { slug: "single-coated", path: "/vacuumglas/single-coated", label: "Single Coated", line: "single-coated", content: BENG_PAGE_CONTENT["single-coated"], image: "/glas-foto/beng-single-coated.webp" },
  { slug: "double-coated", path: "/vacuumglas/double-coated", label: "Double Coated", line: "double-coated", content: BENG_PAGE_CONTENT["double-coated"], image: "/glas-foto/beng-double-coated.webp" },
  { slug: "triple-coated", path: "/vacuumglas/triple-coated", label: "Triple Coated", line: "triple-coated", content: BENG_PAGE_CONTENT["triple-coated"], image: "/glas-foto/beng-triple-coated.webp" },
  { slug: "monu", path: "/vacuumglas/monu", label: "Monu", line: "monu", content: BENG_PAGE_CONTENT.monu, image: "/glas-foto/beng-monu.webp" },
  { slug: "monu-restover-500", path: "/vacuumglas/monu-restover-500", label: "Monu Restover", line: "monu-restover", content: BENG_PAGE_CONTENT["monu-restover"], image: "/glas-foto/beng-monu-restover.webp" },
  { slug: "solar-control", path: "/vacuumglas/solar-control", label: "Solar Control", line: "solar-control", content: BENG_PAGE_CONTENT["solar-control"], image: "/glas-foto/beng-solar-control.webp" },
  { slug: "sun", path: "/vacuumglas/sun", label: "Sun", line: "sun", content: BENG_PAGE_CONTENT.sun, image: "/glas-foto/beng-sun.webp" },
];

export function getBengVariant(slug: string): BengVariant | undefined {
  return BENG_VARIANTS.find((v) => v.slug === slug);
}
